import type { SubscriptionTier } from "./plans";

// ─── Feature key definitions ──────────────────────────────────────────────────

/**
 * Feature keys for component-level subscription gating.
 * Each key maps to a tier requirement and human-readable metadata.
 * Used with `useFeatureGate(featureKey)` to check entitlement inline.
 *
 * Route-level gating continues to use `SubscriptionGate` / `useSubscriptionGate`.
 * This system is for fine-grained feature checks inside components.
 */
export type FeatureKey =
  | "server_creation"              // Create any server community (any paid subscription)
  | "server_creation_beyond_limit" // Create servers beyond the Starter slot limit (Plus+)
  | "advanced_stats"               // Advanced analytics dashboard (Plus+)
  | "priority_queue"               // Priority queue access (Pro+)
  | "premium_server_types"         // High-performance Vultr server SKUs (Plus+)
  | "server_provisioning";         // Auto-provisioned dedicated game servers (any paid)

export type FeatureGateRequiredTier = "any_paid" | "plus" | "pro";

export type FeatureGateConfig = {
  /** Minimum subscription tier required to access this feature */
  requiredTier: FeatureGateRequiredTier;
  /** Short human-readable feature name for upgrade prompt headlines */
  label: string;
  /** One-sentence description explaining what the feature unlocks */
  description: string;
  /**
   * Plan IDs shown in the upgrade prompt card grid.
   * Defaults to all PRICING_PLANS filtered by requiredTier.
   */
  eligiblePlanIds: Array<"starter" | "plus" | "pro">;
};

// ─── Tier ordering ────────────────────────────────────────────────────────────

/** Tiers ordered by rank — higher index = higher tier */
const TIER_RANK: SubscriptionTier[] = [
  "free",
  "starter",
  "plus",
  "pro",
  "enterprise",
];

// ─── Gate definitions ─────────────────────────────────────────────────────────

export const FEATURE_GATES: Record<FeatureKey, FeatureGateConfig> = {
  server_creation: {
    requiredTier: "any_paid",
    label: "Create Server Community",
    description:
      "Create and manage a gaming community for your Hytale server on BlockyNetworks.",
    eligiblePlanIds: ["starter", "plus", "pro"],
  },

  server_creation_beyond_limit: {
    requiredTier: "plus",
    label: "Additional Server Slots",
    description:
      "You've reached your plan's server limit. Upgrade to Founder Plus for up to 5 servers, or Founder Pro for 10.",
    eligiblePlanIds: ["plus", "pro"],
  },

  advanced_stats: {
    requiredTier: "plus",
    label: "Advanced Analytics",
    description:
      "Unlock detailed player analytics, growth charts, and server performance dashboards.",
    eligiblePlanIds: ["plus", "pro"],
  },

  priority_queue: {
    requiredTier: "pro",
    label: "Priority Queue",
    description:
      "Get your support requests handled first with priority queue access on Founder Pro.",
    eligiblePlanIds: ["pro"],
  },

  premium_server_types: {
    requiredTier: "plus",
    label: "Premium Server Types",
    description:
      "Unlock high-performance Vultr instances with more CPU, RAM, and storage for demanding game server workloads.",
    eligiblePlanIds: ["plus", "pro"],
  },

  server_provisioning: {
    requiredTier: "any_paid",
    label: "Managed Server Provisioning",
    description:
      "Auto-provision and manage dedicated Vultr game servers directly from your dashboard.",
    eligiblePlanIds: ["starter", "plus", "pro"],
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true when the given subscription tier meets the feature's
 * minimum tier requirement.
 *
 * - "any_paid" — starter, plus, pro, or enterprise
 * - "plus"     — plus, pro, or enterprise
 * - "pro"      — pro or enterprise only
 */
export function tierMeetsFeatureRequirement(
  tier: SubscriptionTier,
  required: FeatureGateRequiredTier
): boolean {
  if (required === "any_paid") return tier !== "free";
  const tierIdx = TIER_RANK.indexOf(tier);
  if (required === "plus") return tierIdx >= TIER_RANK.indexOf("plus");
  if (required === "pro") return tierIdx >= TIER_RANK.indexOf("pro");
  return false;
}
